import { Link, json, useLoaderData, useNavigate, useParams } from "@remix-run/react";
import { useContext } from "react";
import PageWrapper from "~/components/PageWrapper/PageWrapper";
import ThemeContext from "~/contexts/ThemeContext";
import { urlGen } from "~/helpers/routesHelpers";
import { SiteListObject } from "~/entities/siteEntities";
import { OrderSource } from "~/entities/orderEntities";
import { resolveCateringEntrySite } from "~/helpers/cateringHelpers";
import StoreFinder from "~/modules/sites/components/StoreFinder/StoreFinder";
import Api from "~/api/api";
import { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import OrderContext from "~/contexts/OrderContext";
import BaseLayout from "~/layouts/BaseLayout/BaseLayout";
import { iconMeta } from "~/helpers/remixHelpers";
import { getPersistentSession } from "~/sessions";

export const meta: MetaFunction<typeof loader> = ({ data, matches }) => {
  return [
    iconMeta(matches),
    {
      title: `${
        data?.orgSites?.domainSettings?.title
          ? data?.orgSites?.domainSettings?.title
          : data?.orgSites?.orgName
      } - Sites`,
    },
    {
      name: "description",
      content: data?.orgSites?.domainSettings?.description
        ? `${data?.orgSites?.domainSettings?.description} - Choosing a site for organisation ${data?.orgSites?.orgName}`
        : `Choosing a site for organisation ${data?.orgSites?.orgName}`,
    },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const orgSites = await Api.Site.getOrgSites(request);

  const persistentSession = await getPersistentSession(
    request.headers.get("Cookie"),
  );
  let prevSiteId = persistentSession.get("prevSite");

  if (!orgSites.sites.find((x) => x.id === prevSiteId)) {
    prevSiteId = undefined;
  }

  return json({
    orgSites,
    prevSiteId,
  });
}

const Page = () => {
  const { orgSites, prevSiteId } = useLoaderData<typeof loader>();

  const { template } = useContext(ThemeContext);
  const { orderSource } = useContext(OrderContext);

  const navigate = useNavigate();

  const params = useParams();

  const goToOrder = (
    site: SiteListObject,
    orderTypeCode?: string | null,
    deliveryAddress?: { formattedAddress: string; latitude: number; longitude: number },
  ) => {
    navigate(
      urlGen("Order", {
        orderSource,
        siteId: site.id,
        tableNumber: params.table,
        orderTypeCode: orderTypeCode,
        deliveryAddress: deliveryAddress?.formattedAddress ?? null,
        deliveryLat: deliveryAddress?.latitude ?? null,
        deliveryLng: deliveryAddress?.longitude ?? null,
      }),
    );
  };

  // Style
  const backgroundColor = template.storesScreen.general.backgroundColour;

  return (
    <BaseLayout orgId={orgSites.id}>
      <PageWrapper
        loading={false}
        style={{ backgroundColor, padding: "0 16px" }}
      >
        {/* PageWrapper centres + shrink-wraps its children, so give the picker
            a real centred column width — otherwise it collapses to its content
            and the delivery address input gets cramped/clipped. */}
        <div style={{ width: "100%", maxWidth: 480, margin: "24px auto 0" }}>
        <StoreFinder
          orgId={orgSites.id}
          sites={orgSites.sites}
          prevSiteId={prevSiteId}
          // The /sites picker is the everyday (ONLINE) experience; catering is
          // reached via the separate "Order catering" link.
          orderSource={OrderSource.ONLINE}
          // TODO: pass regionCodes={orgSites.countryCodes} once the org carries a
          // country (see OrgSites in app/entities/siteEntities.ts). Defaults to NZ.
          onSelect={({ site, fulfilment, deliveryAddress }) => {
            const orderTypeCode = site.orderTypes?.find(
              (o: any) => o.fulfillmentType === fulfilment && o.active,
            )?.code;
            // Carry the chosen delivery address through to the order so it isn't
            // lost on the /sites → menu hand-off (the loader writes it to the
            // __order session's deliveryInfo).
            goToOrder(site, orderTypeCode, deliveryAddress);
          }}
        />

        {/* Catering entrance — shown only when a site in the org offers it.
            resolveCateringEntrySite is the shared front-door logic (also used by
            the /catering shareable link and the nav menus): prefer a site with
            available catering pickup, fall back to any active catering site, else
            null → no entrance (a catering site without a menu degrades to the
            graceful empty state). */}
        {(() => {
          const cateringSite = resolveCateringEntrySite(orgSites.sites);
          if (!cateringSite) return null;
          return (
            <div
              style={{
                marginTop: 20,
                textAlign: "center",
                fontSize: 14,
              }}
            >
              Looking for catering?{" "}
              <Link
                to={urlGen("Order", {
                  orderSource: OrderSource.CATERING,
                  siteId: cateringSite.id,
                })}
                style={{
                  color: template.global.button.primaryButtonColour,
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  fontWeight: 600,
                }}
              >
                Order catering →
              </Link>
            </div>
          );
        })()}
        </div>
      </PageWrapper>
    </BaseLayout>
  );
};

export default Page;
